import { zodResolver } from '@hookform/resolvers/zod';
import {
	Button,
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
	FormValidationDialog,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
	TextNumber,
	createFormLabels
} from '@nest/uikit-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { z } from 'zod';
import { useTerminalClient } from '../client-rpc-router';
import { HandlingUnitScanInput } from '../components/HandlingUnitScanInput';
import { StockItemLinkDetail } from '../components/StockItemLinkDetail';
import { StockItemScanInput } from '../components/StockItemScanInput';
import { StorageLocationScanInput } from '../components/StorageLocationScanInput';
import { TerminalScreen } from '../components/TerminalScreen';
import { useNumberParam } from '../helpers';

export default function OutboundDeliveryOrderProcessItemPage() {
	const client = useTerminalClient();
	const lineId = useNumberParam('lineId');
	const outboundDeliveryOrderId = useNumberParam('outboundDeliveryOrderId');
	const navigate = useNavigate();

	const formSchema = useMemo(
		() =>
			z.object({
				stockItem: z.any().nullable(),
				sourceLocation: z.any().nullable(),
				sourceHandlingUnit: z.any().nullable(),
				quantity: z.number().min(0, 'Množství musí být větší než 0')
			}),
		[]
	);

	type FormData = z.infer<typeof formSchema>;

	const [activeTab, setActiveTab] = useState<'stockItem' | 'quantity' | 'sourceHandlingUnit' | 'sourceLocation'>('sourceHandlingUnit');

	const form = useForm<FormData>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			stockItem: null,
			sourceLocation: null,
			sourceHandlingUnit: null,
			quantity: null
		},
		mode: 'onSubmit'
	});

	const { watch, setValue } = form;
	const watchedValues = watch();

	const { data: line, error } = useQuery({
		queryKey: ['outbound-line', lineId],
		queryFn: () =>
			client.outboundDeliveryOrderLines.getById.execute({
				id: lineId!,
				populate: [
					'stockItem',
					'stockItem.item',
					'stockItemReservation',
					'stockItemReservation.stockItemLocation',
					'stockItemReservation.stockItemLocation.handlingUnit',
					'stockItemReservation.stockItemLocation.storageLocation'
				]
			}),
		enabled: !!lineId
	});

	useEffect(() => {
		if (line?.quantityPickRemaining == 0) {
			navigate(`/outbound/delivery/orders/${outboundDeliveryOrderId}/process`);
		}
	}, [line]);

	const loadMutation = useMutation({
		mutationFn: (data: FormData) =>
			client.outboundDeliveryOrders.outboundDeliveryOrderProcessLoadMutation.execute({
				outboundDeliveryOrderLineId: lineId!,
				stockItem: data.stockItem,
				sourceLocation: data.sourceLocation,
				sourceHandlingUnit: data.sourceHandlingUnit,
				quantity: data.quantity
			}),
		onSuccess: () => navigate(`/outbound/delivery/orders/${outboundDeliveryOrderId}/process`)
	});

	// useValidationResultDialog([error, loadMutation.error], {
	// 	control: form.control,
	// 	map: {
	// 		stockItemId: 'stockItem',
	// 		sourceLocationId: 'sourceLocation',
	// 		sourceHandlingUnitId: 'sourceHandlingUnit'
	// 	}
	// });

	const stockItemColorState = () => {
		const stockItem = form.watch('stockItem');
		const suggestedStockItem = line?.stockItem;
		if (stockItem == null) {
			return 'bg-transparent';
		} else if (stockItem.id == suggestedStockItem?.id) {
			return 'bg-green-200 data-[state=active]:bg-green-300';
		} else {
			return 'bg-red-200';
		}
	};

	const sourceLocationColorState = () => {
		const location = form.watch('sourceLocation');
		const suggestedLocation = line?.stockItemReservation?.stockItemLocation.storageLocation;
		if (location == null) {
			return 'bg-transparent';
		} else if (location.id == suggestedLocation?.id) {
			return 'bg-green-200 data-[state=active]:bg-green-300';
		} else {
			return 'bg-red-200';
		}
	};

	const handlingUnitColorState = () => {
		const handlingUnit = form.watch('sourceHandlingUnit');
		const suggestedHandlingUnit = line?.stockItemReservation?.stockItemLocation.handlingUnit;

		if (handlingUnit == null) {
			return 'bg-transparent';
		} else if (handlingUnit.id == suggestedHandlingUnit?.id) {
			return 'bg-green-200 data-[state=active]:bg-green-300';
		} else {
			return 'bg-red-200';
		}
	};

	useEffect(() => {
		if (watchedValues.stockItem) {
			setActiveTab('quantity');
		}
	}, [watchedValues.stockItem]);

	useEffect(() => {
		if (watchedValues.sourceHandlingUnit) {
			if (watchedValues.sourceHandlingUnit.storageLocation) {
				setValue('sourceLocation', watchedValues.sourceHandlingUnit.storageLocation);
				setActiveTab('stockItem');
			} else {
				setActiveTab('sourceLocation');
			}
		}
	}, [watchedValues.sourceHandlingUnit, setValue]);

	useEffect(() => {
		if (watchedValues.sourceLocation) {
			setActiveTab('stockItem');
		}
	}, [watchedValues.sourceLocation]);

	const confirmLoad = () => {
		loadMutation.mutateAsync(form.getValues());
	};

	const formLabels = createFormLabels<FormData>(form.control, {
		stockItem: 'Položka',
		sourceLocation: 'Lokace',
		sourceHandlingUnit: 'Paleta',
		quantity: 'Množství'
	});

	const bottomContent = (
		<Button variant="default" onClick={confirmLoad} disabled={loadMutation.isPending} className="w-full">
			{loadMutation.isPending ? 'Zpracovávám...' : 'Potvrdit položku'}
		</Button>
	);

	return (
		<TerminalScreen showBackButton topBar={<StockItemLinkDetail stockItemId={line?.stockItem?.id} />} bottomContent={bottomContent}>
			<div className="h-full flex flex-col">
				<Form {...form}>
					<FormValidationDialog errors={[error, loadMutation.error]} control={form.control} labels={formLabels} />

					<form className="h-full flex flex-col">
						<Table>
							<TableBody>
								<TableRow>
									<TableHead className="w-1/2">Kód</TableHead>
									<TableCell className="w-1/2 font-mono">{line?.stockItem?.item.code}</TableCell>
								</TableRow>
								<TableRow>
									<TableHead className="w-1/2">Lokace</TableHead>
									<TableCell className="w-1/2">{line?.stockItemReservation?.stockItemLocation.storageLocation.name}</TableCell>
								</TableRow>
								<TableRow>
									<TableHead className="w-1/2">Paleta</TableHead>
									<TableCell className="w-1/2">{line?.stockItemReservation?.stockItemLocation.handlingUnit?.code}</TableCell>
								</TableRow>
								{/* <TableRow>
									<TableHead className="w-1/2">Šarže</TableHead>
									<TableCell className="w-1/2">{line?.stockItemReservation?.stockItemLocation.batch}</TableCell>
								</TableRow> */}
								<TableRow>
									<TableHead className="w-1/2">Zbývá</TableHead>
									<TableCell className="w-1/2">{line?.stockItemReservation?.quantity ?? line?.quantityPickRemaining}</TableCell>
								</TableRow>
							</TableBody>
						</Table>

						<div className="flex-1 overflow-auto p-4">
							<Tabs value={activeTab} onValueChange={v => setActiveTab(v)} className="w-full">
								<TabsList className="grid grid-cols-4 w-full">
									<TabsTrigger value="sourceHandlingUnit" className={handlingUnitColorState()}>
										Paleta
									</TabsTrigger>
									<TabsTrigger value="sourceLocation" className={sourceLocationColorState()}>
										Lokace
									</TabsTrigger>
									<TabsTrigger value="stockItem" className={stockItemColorState()}>
										SKU
									</TabsTrigger>
									<TabsTrigger value="quantity">Množství</TabsTrigger>
								</TabsList>

								<TabsContent value="stockItem" className="space-y-4 p-2">
									<FormField
										control={form.control}
										name="stockItem"
										render={({ field }) => (
											<FormItem>
												<FormLabel className="sr-only">{formLabels.stockItem}</FormLabel>
												<FormControl>
													<StockItemScanInput value={field.value} autoFocus placeholder="Naskenujte položku..." onChange={field.onChange} />
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
								</TabsContent>

								<TabsContent value="quantity" className="space-y-4 p-2">
									<FormField
										control={form.control}
										name="quantity"
										render={({ field }) => (
											<FormItem>
												<FormLabel className="sr-only">{formLabels.quantity}</FormLabel>
												<FormControl>
													<TextNumber value={field.value} onChange={field.onChange} placeholder="Množství" autoFocus={true} />
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
								</TabsContent>

								<TabsContent value="sourceHandlingUnit" className="space-y-4 p-2">
									<FormField
										control={form.control}
										name="sourceHandlingUnit"
										render={({ field }) => (
											<FormItem>
												<FormLabel className="sr-only">{formLabels.sourceHandlingUnit}</FormLabel>
												<FormControl>
													<HandlingUnitScanInput
														value={field.value}
														autoFocus
														placeholder="Naskenujte klec..."
														onChange={value => field.onChange(value)}
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
								</TabsContent>

								<TabsContent value="sourceLocation" className="space-y-4 p-2">
									<FormField
										control={form.control}
										name="sourceLocation"
										render={({ field }) => (
											<FormItem>
												<FormLabel className="sr-only">{formLabels.sourceLocation}</FormLabel>
												<FormControl>
													<StorageLocationScanInput
														value={field.value}
														autoFocus
														placeholder="Naskenujte lokaci..."
														onChange={value => field.onChange(value)}
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
								</TabsContent>
							</Tabs>
						</div>
					</form>
				</Form>
			</div>
		</TerminalScreen>
	);
}
